// ── Schema Markup Component ───────────────────────────────────────────────────
// Injects JSON-LD structured data into the page head for Google rich results.
// Usage: <SchemaMarkup schema={object} /> or <SchemaMarkup schema={[array]} />

import { Helmet } from 'react-helmet-async';

const SchemaMarkup = ({ schema }) => {
  if (!schema) return null;
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(Array.isArray(schema) ? schema : [schema])}
      </script>
    </Helmet>
  );
};

export default SchemaMarkup;

// ── Schema builders ───────────────────────────────────────────────────────────
// Call these to build schema objects, then pass to <SchemaMarkup schema={...} />

const BASE_URL = 'https://dreamybedtime.com';
const LOGO_URL = `${BASE_URL}/logo192.png`;

// Organization — used on homepage
export const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Dreamy Bedtime',
  url: BASE_URL,
  logo: LOGO_URL,
  sameAs: [
    'https://www.instagram.com/dreamy_bedtime',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: `${BASE_URL}/contact`,
    availableLanguage: ['English'],
  },
});

// SoftwareApplication — used on homepage
export const buildSoftwareSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Dreamy Bedtime',
  url: BASE_URL,
  applicationCategory: 'EducationApplication',
  operatingSystem: 'Web, iOS, Android',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: '5 free personalised bedtime stories per month. No credit card required.',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '127',
    bestRating: '5',
    worstRating: '1',
  },
  description: 'Personalised bedtime stories for children — starring your child by name. Generate unique stories in seconds with beautiful illustrations and narration.',
  screenshot: LOGO_URL,
  featureList: [
    'Personalised stories with child\'s name',
    'Stories in 11 languages',
    'Natural narration and voice cloning',
    'Beautiful AI illustrations',
    'Story series',
    'Reading reports',
  ],
});

// WebSite — enables sitelinks search box in Google
export const buildWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Dreamy Bedtime',
  url: BASE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/community?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

// FAQ — used on homepage, pricing, SEO pages
export const buildFaqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: a,
    },
  })),
});

// Breadcrumb — used on all SEO landing pages
export const buildBreadcrumbSchema = (crumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map(({ name, path }, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name,
    item: `${BASE_URL}${path}`,
  })),
});

// Product — used on pricing page
export const buildProductSchema = ({ name, description, price, currency = 'USD' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  brand: {
    '@type': 'Brand',
    name: 'Dreamy Bedtime',
  },
  offers: {
    '@type': 'Offer',
    price,
    priceCurrency: currency,
    availability: 'https://schema.org/InStock',
    url: `${BASE_URL}/pricing`,
  },
});

// Article — used on blog posts and community stories
export const buildArticleSchema = ({ title, description, url, imageUrl, datePublished, dateModified, authorName = 'Dreamy Bedtime' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  url: `${BASE_URL}${url}`,
  image: imageUrl || LOGO_URL,
  datePublished,
  dateModified: dateModified || datePublished,
  author: {
    '@type': 'Person',
    name: authorName,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Dreamy Bedtime',
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${BASE_URL}${url}`,
  },
});

// HowTo — used on blog posts that explain steps
export const buildHowToSchema = ({ name, description, steps, totalTime }) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name,
  description,
  totalTime,
  step: steps.map(({ name: stepName, text, url }, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: stepName,
    text,
    ...(url ? { url: `${BASE_URL}${url}` } : {}),
  })),
});

// Review — used when you have user reviews to display
export const buildReviewSchema = (reviews) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: reviews.map((r, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: r.rating || 5,
        bestRating: 5,
      },
      author: {
        '@type': 'Person',
        name: r.author,
      },
      reviewBody: r.body,
      itemReviewed: {
        '@type': 'SoftwareApplication',
        name: 'Dreamy Bedtime',
      },
    },
  })),
});
